import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Popover,
  useMediaQuery,
} from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { API_BASE } from '../../../../config/env';
import { httpService } from '../../../../utility/httpService';
import { httpConstants } from '../../../../constants';
import ShowToast, { ToastTypeEnum } from '../../../../common/components/ShowToast';
import { formatLabel, formatTimestamp } from '../../../../common/utils';

import SearchModal from './SearchModal';
import ChatListingHeader from './ChatListingHeader';
import RenameSessionModal from '../modal/RenameSessionModal';
import DeleteSessionModal from '../modal/DeleteSessionModal';
import { ChatSession, IdeationIntake } from '../../types';
import { getFieldKeyFromCategory } from '../../../NewIdeationXPLR/shared/constants';
import SkeletonList from './SkeletonList';
import MobileChatStrip from './MobileView';

// Types are now imported from ./types

interface ChatListingSidebarProps {
  sessions: ChatSession[];
  elasticChatSessions: ChatSession[];
  currentChatId: string;
  onSelectChat: (
    chatId: string,
    fieldType?: string,
    sessionName?: string,
    conversationType?: string
  ) => void;
  onNewChat: () => void;
  onRename?: (sessionId: string, newTitle: string) => void;
  onDelete?: (sessionId: string) => void;
  onNewIdeation: () => void;
  onCategoryChange?: (category: string) => void;
  onSearchChange?: (searchQuery: string) => void;
  onRefetch?: () => void;
  selectedCategory?: string;
  setuseCaseDetails?: (details: Record<string, any>) => void;
  useCaseDetails?: Record<string, any>;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  isInitialLoading?: boolean;
  hasReachedEnd?: boolean;
  searchFromQuery?: string;
  sortBy?: string;
  setSortBy?: (sort: string) => void;
  onSortChange?: (sortBy: string) => void;
  setSelectedElasticChat?: (chat: any) => void;
  setSessions?: (chat: any) => void;
}

const ChatListingSidebar: React.FC<ChatListingSidebarProps> = ({
  sessions,
  elasticChatSessions,
  currentChatId,
  onSelectChat,
  onNewChat,
  searchFromQuery,
  onNewIdeation,
  onCategoryChange = () => {},
  onSearchChange,
  onRefetch,
  selectedCategory = '',
  setuseCaseDetails,
  useCaseDetails,
  onLoadMore,
  isLoadingMore = false,
  isInitialLoading = false,
  hasReachedEnd = false,
  sortBy = '',
  setSortBy = () => {},
  onSortChange,
  setSelectedElasticChat = () => {},
  setSessions = () => {},
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchFromQuery || '');
  const [openSearchModal, setOpenSearchModal] = useState(false);
  const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(null);
  const [modalSession, setModalSession] = useState<ChatSession | null>(null);
  const [openRenameModal, setOpenRenameModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [category, setCategory] = useState(selectedCategory);
  const [intakeAnchorEl, setIntakeAnchorEl] = useState<null | HTMLElement>(null);
  const [hoveredIntakes, setHoveredIntakes] = useState<IdeationIntake[]>([]);
  const [conversationType, setConversationType] = useState<string>('ideation_xplr');
  const intakeCloseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };
  const isMobile = useMediaQuery('(max-width:900px)');
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, session: ChatSession) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedSession(session);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSession(null);
  };

  const handleRename = (event: any) => {
    event?.stopPropagation?.();
    if (selectedSession) {
      setModalSession(selectedSession);
      setRenameValue(selectedSession.use_case_title);
      setOpenRenameModal(true);
    }
    handleMenuClose();
  };

  const handleDelete = (event: any) => {
    event?.stopPropagation?.();
    if (selectedSession) {
      setModalSession(selectedSession);
      setOpenDeleteModal(true);
    }
    handleMenuClose();
  };

  const handleRenameConfirm = async () => {
    console.log('handleRenameConfirm called', modalSession, renameValue);
    // Character limit validation
    if (renameValue.trim().length > 100) {
      ShowToast({
        message: 'Session name cannot exceed 100 characters',
        type: ToastTypeEnum.Error,
      });
      return;
    }
    if (modalSession && renameValue.trim()) {
      const organizationId = localStorage.getItem('current_organization') || '';
      const url = `${API_BASE}/api/v1/ideation/query-history/${modalSession.conversation_id}/session-name/`;
      console.log('Rename API call:', url, {
        session_name: renameValue.trim(),
        organization_id: organizationId,
        field_type: getFieldKeyFromCategory[selectedCategory],
      });

      try {
        const response = await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, {
          session_name: renameValue.trim(),
          organization_id: organizationId,
          field_type: modalSession?.field_type,
          conversation_type: modalSession?.conversation_type,
        });
        console.log('Rename API response:', response);

        if (setuseCaseDetails && useCaseDetails && modalSession.conversation_id === currentChatId) {
          setuseCaseDetails({ ...useCaseDetails, title: renameValue.trim() });
        }

        if (onRefetch) {
          console.log('Calling onRefetch');
          onRefetch();
        }
        setOpenRenameModal(false);
        setRenameValue('');
      } catch (error) {
        console.error('Failed to rename session:', error);
      }
    } else {
      console.log('Closing rename modal - no session or empty name');
      setOpenRenameModal(false);
      setRenameValue('');
    }
  };
  const capitalizeName = (name: string) => {
    if (!name) return name;
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleDeleteConfirm = async () => {
    console.log('handleDeleteConfirm called', modalSession);
    if (modalSession) {
      const organizationId = localStorage.getItem('current_organization') || '';
      const url = `${API_BASE}/api/v1/ideation/query-history/${modalSession.conversation_id}/delete/`;
      console.log('Delete API call:', url);

      try {
        const response = await httpService<any>(httpConstants.METHOD_TYPE.DELETE, url, {
          organization_id: organizationId,
          field_type: modalSession?.field_type,
          conversation_type: modalSession?.conversation_type,
        });
        console.log('Delete API response:', response);
        if (onRefetch) {
          console.log('Calling onRefetch');
          setSelectedElasticChat('');
          onRefetch();
        }
        setOpenDeleteModal(false);
        if (modalSession.conversation_id === currentChatId) {
          onNewChat();
          onNewIdeation();
        }
        ShowToast({ message: 'Session deleted successfully', type: ToastTypeEnum.Success });
      } catch (error) {
        console.error('Failed to delete session:', error);
      }
    } else {
      console.log('Closing delete modal - no session');
      ShowToast({ message: 'Failed to delete session', type: ToastTypeEnum.Error });

      setOpenDeleteModal(false);
    }
  };

  const handleIntakeHover = (
    event: React.MouseEvent<HTMLElement>,
    intakes: IdeationIntake[],
    conversation_type: string = ''
  ) => {
    if (intakes && intakes.length > 0) {
      if (intakeCloseTimeoutRef.current) {
        clearTimeout(intakeCloseTimeoutRef.current);
        intakeCloseTimeoutRef.current = null;
      }
      setIntakeAnchorEl(event.currentTarget);
      setHoveredIntakes(intakes);
      setConversationType(conversation_type);
    }
  };

  const handleIntakeClose = () => {
    intakeCloseTimeoutRef.current = setTimeout(() => {
      setIntakeAnchorEl(null);
      setHoveredIntakes([]);
    }, 100);
  };

  const handleIntakePopoverEnter = () => {
    if (intakeCloseTimeoutRef.current) {
      clearTimeout(intakeCloseTimeoutRef.current);
      intakeCloseTimeoutRef.current = null;
    }
  };

  const collapsedWidth = 56;
  const expandedWidth = 350;

  useEffect(() => {
    setCategory(selectedCategory);
  }, [selectedCategory]);

  // Handle scroll detection for infinite scroll
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || isCollapsed || !onLoadMore || isLoadingMore) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // Trigger load more when user is 200px from bottom
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        onLoadMore();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [onLoadMore, isLoadingMore, isCollapsed]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isCollapsed || !currentChatId) return;
    const selectedEl = container.querySelector(
      `[data-conversation-id="${currentChatId}"]`
    ) as HTMLElement | null;
    if (selectedEl) {
      const elementTop = selectedEl.offsetTop;
      const elementBottom = elementTop + selectedEl.offsetHeight;
      const containerScrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      if (elementTop < containerScrollTop) {
        container.scrollTop = elementTop;
      } else if (elementBottom > containerScrollTop + containerHeight) {
        container.scrollTop = elementBottom - containerHeight;
      }
    }
  }, [currentChatId, sessions, isCollapsed]);

  const mapProcessName = useMemo(
    () => (processName: string) => {
      if (processName.includes('Solution')) return processName;
      if (!processName) return processName;
      const exceptions = new Set(['for', 'of']);
      return processName
        .split(/[\s_]+/)
        .filter(Boolean)
        .map((word) => {
          const lowerWord = word.toLowerCase();
          return exceptions.has(lowerWord)
            ? lowerWord
            : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
    },
    []
  );

  // Safety-net deduplication: even if the data layer momentarily produces
  // duplicate entries (e.g., concurrent loadSessions calls), the sidebar
  // will never render more than one card per conversation.
  const dedupedSessions = useMemo(() => {
    const seenKeys = new Set<string>();
    return sessions.filter((s) => {
      const key = s.conversation_id || s.id;
      if (!key || seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });
  }, [sessions]);
  useEffect(() => {
    setIsCollapsed(isMobile);
  }, [isMobile]);

  return (
    <>
      {isMobile ? (
        <MobileChatStrip
          sessions={dedupedSessions}
          currentChatId={currentChatId}
          onSelectChat={onSelectChat}
          onNewChat={onNewChat}
          onNewIdeation={onNewIdeation}
          onOpenSearch={() => setOpenSearchModal(true)}
          category={category}
          sortBy={sortBy}
          onCategoryChange={(newCategory) => {
            setCategory(newCategory);
            if (onCategoryChange) {
              setSelectedElasticChat('');
              onCategoryChange(newCategory);
              setSearchQuery('');
            }
          }}
          onSortChange={(newSortBy) => {
            setSortBy(newSortBy);
            if (onSortChange) {
              onSortChange(newSortBy);
            }
          }}
        />
      ) : (
        <Box
          sx={{
            width: isCollapsed ? collapsedWidth : expandedWidth,
            height: '100%',
            backgroundColor: '#ffffff',
            borderRight: '1px solid #EBEBEB',
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.3s ease',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {/* Header Section */}
          {!isCollapsed ? (
            <ChatListingHeader
              category={category}
              sortBy={sortBy}
              onCategoryChange={(newCategory) => {
                setCategory(newCategory);
                if (onCategoryChange) {
                  setSelectedElasticChat('');
                  onCategoryChange(newCategory);
                  setSearchQuery('');
                }
              }}
              onSortChange={(newSortBy) => {
                setSortBy(newSortBy);
                if (onSortChange) {
                  onSortChange(newSortBy);
                }
              }}
              onNewChat={onNewChat}
              onNewIdeation={onNewIdeation}
              isSearchOpen={openSearchModal}
              onOpenSearch={() => setOpenSearchModal(true)}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pt: 2,
                gap: 2,
              }}
            >
              <Tooltip title="Create New Knowledge" placement="right">
                <IconButton
                  onClick={() => {
                    onNewChat();
                    onNewIdeation();
                  }}
                  size="small"
                  sx={{
                    color: '#1A55F9',
                    border: '1px solid #1A55F9',
                    borderRadius: '8px',
                    '&:hover': { backgroundColor: '#eff6ff' },
                  }}
                >
                  <img src="/images/add-new.png" alt="Add" width={13} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Chat List */}
          <Box
            ref={scrollContainerRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: isCollapsed ? 1 : 2,
              py: 1,
              position: 'relative',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: '#d1d5db', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { background: '#9ca3af' },
            }}
          >
            {!isCollapsed && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                {isInitialLoading ? (
                  <SkeletonList />
                ) : dedupedSessions?.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                      py: 2,
                      height: '100%',
                    }}
                  >
                    <Box>
                      <img
                        src="/images/empty-sessions.png"
                        alt="no sessions"
                        width={64}
                        height={64}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: '#131426', fontSize: '15px', fontWeight: 500 }}
                    >
                      No knowledge objects found.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#131426', fontSize: '15px' }}>
                      Create your first knowledge object.
                    </Typography>
                  </Box>
                ) : (
                  dedupedSessions.map((session) => (
                    <Box
                      key={session.id}
                      data-conversation-id={session.conversation_id}
                      onMouseEnter={() => setHoveredSessionId(session.id)}
                      onMouseLeave={() => setHoveredSessionId(null)}
                      onClick={() =>
                        onSelectChat(
                          session?.conversation_id,
                          session?.field_type || '',
                          session?.sessionName || session?.session_name || '',
                          session?.conversation_type || ''
                        )
                      }
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        p: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: '#ffffff',
                        borderColor:
                          currentChatId === session.conversation_id ? '#1A55F9' : '#e5e7eb',
                        position: 'relative',
                      }}
                    >
                      <Box>
                        {session.sequential_id &&
                          session.sequential_id.toLowerCase() !== 'none' && (
                            <Typography variant="body2" sx={{ color: '#878D96', fontSize: '10px' }}>
                              {session.sequential_id}
                            </Typography>
                          )}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 1,
                          }}
                        >
                          <Tooltip
                            title={session.use_case_title || 'Untitled Process'}
                            placement="bottom-start"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  backgroundColor: '#ffffff',
                                  color: '#000000',
                                  fontSize: '13px',
                                  boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
                                  borderRadius: '6px',
                                },
                              },
                              arrow: { sx: { color: '#ffffff' } },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: '#313131',
                                fontSize: '15px',
                                mb: 1,
                                lineHeight: 1.4,
                                flex: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                              }}
                            >
                              {session.use_case_title || 'Untitled Process'}
                            </Typography>
                          </Tooltip>
                          <Box
                            sx={{
                              position: 'relative',
                              alignItems: 'center',
                              display: 'flex',
                              flexShrink: 0,
                            }}
                          >
                            {hoveredSessionId === session.id && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, session)}
                                sx={{
                                  color: '#6b7280',
                                  padding: '2px',
                                  '&:hover': { backgroundColor: '#e5e7eb' },
                                }}
                              >
                                <MoreHoriz fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ flex: 1, pr: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#313131',
                              display: 'block',
                              fontSize: '11px',
                              fontWeight: 400,
                              fontFamily: 'Poppins',
                              mb: 0.5,
                            }}
                          >
                            {session?.updated_by
                              ? `updated by ${capitalizeName(session.updated_by)} `
                              : `created by ${capitalizeName(session.created_by)} `}
                            {formatTimestamp(session.updated_at)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#313131',
                                fontSize: '11px',
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontWeight: 400,
                                fontFamily: 'Poppins',
                                mt: 0.2,
                                flexShrink: 1,
                                minWidth: 0,
                                maxWidth: '100%',
                              }}
                            >
                              {mapProcessName(session.usedInProcess)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#313131',
                                fontSize: '11px',
                                fontWeight: 400,
                                fontFamily: 'Poppins',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                mt: 0.2,
                              }}
                            >
                              {' · '}
                              Used in
                            </Typography>
                            <Box
                              onMouseEnter={(e) =>
                                handleIntakeHover(
                                  e,
                                  session.ideation_intake,
                                  session?.conversation_type
                                )
                              }
                              onMouseLeave={handleIntakeClose}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#313131',
                                borderRadius: '50%',
                                minWidth: '18px',
                                height: '18px',
                                fontSize: '11px',
                                fontWeight: 400,
                                fontFamily: 'Poppins',
                                px: 0.5,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                              }}
                            >
                              {session.ideation_intake?.length || 0} intake
                            </Box>
                          </Box>
                          <Box>
                            {session.conversation_type && (
                              <Chip
                                label={
                                  session.conversation_type === 'solution_xplr'
                                    ? 'Solution XPLR'
                                    : 'Ideation XPLR'
                                }
                                size="small"
                                sx={{
                                  fontFamily: 'Poppins',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  height: '18px',
                                  color: '#737373',
                                  backgroundColor: 'transparent',
                                  border: '1px solid #737373',
                                  borderRadius: '4px',
                                  mt: 0.5,
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl) && selectedSession?.id === session.id}
                          onClose={handleMenuClose}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          PaperProps={{
                            sx: {
                              borderRadius: '8px',
                              boxShadow:
                                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              mt: 1,
                            },
                          }}
                        >
                          <MenuItem
                            onClick={handleRename}
                            sx={{ fontFamily: 'Poppins', fontSize: '14px' }}
                          >
                            Rename
                          </MenuItem>
                          <MenuItem
                            onClick={handleDelete}
                            sx={{ fontFamily: 'Poppins', fontSize: '14px', color: '#EF4444' }}
                          >
                            Delete
                          </MenuItem>
                        </Menu>
                      </Box>
                    </Box>
                  ))
                )}

                {isLoadingMore && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '14px' }}>
                      Loading more sessions...
                    </Typography>
                  </Box>
                )}
                {!isLoadingMore && hasReachedEnd && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: '#9ca3af', fontSize: '14px', fontStyle: 'italic' }}
                    >
                      No more sessions available
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Collapse/Expand Button at Bottom */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              borderTop: '1px solid #e5e7eb',
            }}
          >
            <Tooltip title={isCollapsed ? 'Expand' : 'Collapse'} placement="right">
              <IconButton
                onClick={toggleCollapse}
                size="small"
                sx={{
                  color: '#6b7280',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#f9fafb', borderColor: '#d1d5db' },
                }}
              >
                {isCollapsed ? (
                  <img src="/images/frontbtndata.svg" alt="" />
                ) : (
                  <img src="/images/backbtndata.svg" alt="" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Intake Details Popover */}
      <Popover
        open={Boolean(intakeAnchorEl) && hoveredIntakes.length > 0}
        anchorEl={intakeAnchorEl}
        onClose={handleIntakeClose}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ pointerEvents: 'none' }}
        PaperProps={{
          sx: { pointerEvents: 'auto', backgroundColor: '#ffffff', mt: 0.1 },
          onMouseEnter: handleIntakePopoverEnter,
          onMouseLeave: handleIntakeClose,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '250px', p: 1 }}>
          {hoveredIntakes.map((intake, index) => (
            <Box
              key={intake.id || index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 0.75,
                backgroundColor: '#FFFFFF',
                borderRadius: '6px',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#000000',
                  }}
                >
                  {conversationType === 'solution_xplr' ? 'Solution' : 'Ideation'} XPLR &gt;{' '}
                  {intake?.level2_name || intake?.ideation_name || 'N/A'} &gt;{' '}
                  {formatLabel(intake.type || '') || 'N/A'}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#6B7280',
                  ml: 2,
                }}
              >
                {intake.version || 'N/A'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      {/* Rename Modal */}
      <RenameSessionModal
        open={openRenameModal}
        onClose={() => setOpenRenameModal(false)}
        renameValue={renameValue}
        onRenameValueChange={setRenameValue}
        onConfirm={handleRenameConfirm}
      />

      {/* Delete Confirmation Modal */}
      <DeleteSessionModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
      />

      <SearchModal
        openSearchModal={openSearchModal}
        setOpenSearchModal={setOpenSearchModal}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        chatsData={elasticChatSessions}
        setCategory={setCategory}
        onCategoryChange={onCategoryChange}
        onSelectChat={onSelectChat}
        isLoadingMore={isLoadingMore}
        hasReachedEnd={hasReachedEnd}
        onLoadMore={onLoadMore}
        onSearchChange={onSearchChange}
        setSelectedElasticChat={setSelectedElasticChat}
        setSessions={setSessions}
      />
    </>
  );

};

export default ChatListingSidebar;
